import React, { useContext, useState } from "react";
import moment from "moment";
import { NavLink, useNavigate } from "react-router-dom";
import { AuthContext, IntlContext, ListContext } from "context";
import dataFetcher, { BookingInterface, BookingStatus } from "hooks/booking";
import { SUBCATEGORY_TO_CATEGORY, SubCategory } from "types/subCategory";
import { ListItemWithIds } from "components/List";
import Image from "components/File/Image";
import Button from "ui/Button";
import { Role } from "types/user";
import RatingModal from "app/Booking/List/RatingModal";
import useQueryString from "helpers/useQueryString";
import RefuseServiceModal from "./RefuseBookingModal";
import ValidateBookingModal from "./ValidateBookingModal";
import ConfirmModal from "ui/Modal/ConfirmModal";
import { BookingValidationStatus } from "hooks/bookingValidation";
import { downloadInvoice } from "hooks/invoice";
import fileDownload from "js-file-download";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import formatReferencesValues from "helpers/form/formatReferencesValues";
import { useReferences } from "hooks/reference";
import { buildRoute } from "routes";
import { toast } from "sonner";

const List: React.FC = () => {
    const intl = useContext(IntlContext);
    const { account } = useContext(AuthContext);
    const { refreshData } = useContext(ListContext);
    const reference = useReferences();
    const [isModalHidden, setIsModalHidden] = useState<boolean>(true);
    const [isHiddenValidateServiceModal, setIsHiddenValidateServiceModal] = useState<boolean>(true);
    const [isHiddenRefuseServiceModal, setIsHiddenRefuseServiceModal] = useState<boolean>(true);
    const [showCancelBookingModal, setShowCancelBookingModal] = useState<boolean>(false);
    const [selectedBookingId, setSelectedBookingId] = useState<string>();
    const queryString = useQueryString();
    const navigate = useNavigate();

    const formatDate = (date: string) => {
        const dateFormatted = moment(date);

        return dateFormatted.locale("fr").format("LL");
    };

    const handleDownload = (booking: BookingInterface) => {
        downloadInvoice(booking.id).then((response) => {
            fileDownload(response.content, response.name);
        });
    };

    const handleDownloadSalesSuccession = (booking: BookingInterface) => {
        dataFetcher.downloadSalesSuccession({ booking_id: booking.id }).then((response) => {
            fileDownload(response.content, response.name);
        });
    };

    const handleConfirmCancelModal = async () => {
        const filterStatus = queryString.get("filter_status");
        setShowCancelBookingModal(false);

        if (
            (filterStatus === BookingStatus.PENDING || filterStatus === BookingStatus.TO_VALIDATE) &&
            selectedBookingId
        ) {
            await dataFetcher
                .cancel(selectedBookingId)
                .then(async () => {
                    toast.success(
                        intl.formatMessage("our_services.booking.confirm.message.transmitting_cesu.success"),
                    );
                    refreshData();
                })
                .catch(() => {
                    toast.error(
                        intl.formatMessage("our_services.booking.confirm.message.transmitting_cesu.error"),
                    );
                });
        }

        if (filterStatus === BookingStatus.TO_VALIDATE) {
            setIsHiddenRefuseServiceModal(false);
        }
    };

    return (
        <React.Fragment>
            <ListItemWithIds
                grid={12}
                has_gutter={3}
                classContainerItem={"our_services_container grid__col--4 grid__col--large-6 grid__col--medium-12"}
                rendererHeader={(booking: BookingInterface) => {
                    const userClient = account?.resources?.client;
                    const userProvider = account?.resources?.provider;
                    const startTimeSplit = booking.start_time.split(":")[0];
                    const bookingDateTime = moment(
                        moment(booking.date).format("YYYY-MM-DD") + " " + booking.start_time,
                    );
                    const previousPath = window.location.pathname;
                    const subCategory: SubCategory = booking.subcategory;
                    let role: Role | undefined = undefined;
                    let hasRated: boolean = false;

                    if (userClient && userClient[0].id === booking.client_id) {
                        role = Role.CLIENT;

                        if (booking.notes.provider) {
                            hasRated = true;
                        }
                    }

                    if (userProvider && userProvider[0].id === booking.provider_id) {
                        role = Role.PROVIDER;

                        if (booking.notes.client) {
                            hasRated = true;
                        }
                    }

                    return (
                        <div key={`heading-${booking.id}`} className="items">
                            <div className="flex flex--direction-column flex--gap flex--justify-content-space-between items--description padding--3">
                                <div className="items--subcategory margin--0">
                                    <h3 className={"text-color--" + booking.category.toString().toLocaleLowerCase()}>
                                        {intl.formatMessage(
                                            "category." + booking.category.toString().toLocaleLowerCase(),
                                        )}
                                    </h3>
                                    {subCategory && (
                                        <span
                                            className={
                                                "text-color--white bg--" +
                                                booking.category.toString().toLocaleLowerCase() +
                                                "-base"
                                            }
                                        >
                                            {intl.formatMessage(
                                                "subcategory." +
                                                    SUBCATEGORY_TO_CATEGORY[subCategory].toLocaleLowerCase() +
                                                    "." +
                                                    subCategory.toLocaleLowerCase() +
                                                    ".label",
                                            )}
                                            {subCategory == SubCategory.HOMEWORK &&
                                                " : " +
                                                    formatReferencesValues(
                                                        reference,
                                                        "scholar_subject",
                                                        booking.data.scholar_subjects,
                                                    ) +
                                                    " - " +
                                                    formatReferencesValues(
                                                        reference,
                                                        "scholar_level",
                                                        booking.data.academic_level,
                                                    )}
                                            {subCategory == SubCategory.FOREIGN_LANGUAGES &&
                                                " : " +
                                                    formatReferencesValues(
                                                        reference,
                                                        "foreign_languages",
                                                        booking.data.foreign_languages,
                                                    )}
                                            {subCategory == SubCategory.OTHER_LESSON &&
                                                " : " +
                                                    formatReferencesValues(
                                                        reference,
                                                        "activity",
                                                        booking.data.activities,
                                                    )}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <div className="flex">
                                        <Icon icon={{ prefix: "fas", iconName: "calendar-alt" }} />
                                        &nbsp;
                                        {formatDate(booking.date)}
                                    </div>
                                    <div>
                                        <Icon icon={{ prefix: "fas", iconName: "clock" }} />
                                        &nbsp;
                                        {intl.formatMessage("booking.list.services.label.time", {
                                            hour_start: startTimeSplit,
                                            hour_end: parseInt(startTimeSplit) + booking.nb_hours,
                                            time: booking.nb_hours,
                                        })}
                                    </div>
                                    {role === Role.PROVIDER && (
                                        <React.Fragment>
                                            <div className="flex">
                                                <Icon icon={{ prefix: "fas", iconName: "user" }} />
                                                &nbsp;
                                                <span>
                                                    chez{" "}
                                                    {intl.formatMessage(
                                                        `generic.label.gender.abbreviated.${booking.client.gender}`,
                                                    )}{" "}
                                                    {booking.client.first_name}{" "}
                                                    {booking.client.last_name.toLocaleUpperCase()}{" "}
                                                </span>
                                                {booking.client.has_pet && (
                                                    <span className="btn-tooltip margin-left--1">
                                                        <Icon
                                                            icon={{ prefix: "fas", iconName: "dog" }}
                                                            className="fa-stack-1x margin--0"
                                                        />

                                                        <span className="btn-tooltip__content">
                                                            {intl.formatMessage("booking.list.services.label.pet_type")}
                                                            {booking.client.pet_type?.map((pet_type, key) => {
                                                                let petsTypeLength =
                                                                    booking.client.pet_type?.length ?? 0;
                                                                let translatedPetType = reference.get(
                                                                    "pet_type",
                                                                    pet_type,
                                                                )?.value_normalized;

                                                                if (key + 1 < petsTypeLength) {
                                                                    return `${translatedPetType}, `;
                                                                }

                                                                return `${translatedPetType}`;
                                                            })}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <span className="icon icon-loc" style={{ marginLeft: "-1px" }}></span>
                                                &nbsp;
                                                <span>{booking.client.address}</span>
                                            </div>
                                        </React.Fragment>
                                    )}
                                </div>

                                {booking?.booking_comment && (
                                    <blockquote className="items--provider_description text-color--primary">
                                        <p> {booking.booking_comment} </p>
                                    </blockquote>
                                )}

                                {role !== Role.PROVIDER && (
                                    <p>
                                        <NavLink
                                            to={buildRoute.provider(booking.service_id)}
                                            state={{ previousPath }}
                                            className="booking_content_provider"
                                        >
                                            {booking.provider.photo ? (
                                                <Image
                                                    id={booking.provider.photo}
                                                    className="booking_content_provider--image"
                                                />
                                            ) : (
                                                <div className="container_initial_provider">
                                                    <span>
                                                        {booking.provider.first_name.charAt(0)}{" "}
                                                        {booking.provider.last_name.charAt(0)}{" "}
                                                    </span>
                                                </div>
                                            )}
                                            <div>
                                                {intl.formatMessage("booking.list.links.label.service_by")}
                                                <br />
                                                <i>
                                                    <b>
                                                        {booking.provider.first_name + " " + booking.provider.last_name}
                                                    </b>
                                                </i>
                                            </div>
                                        </NavLink>
                                    </p>
                                )}
                                <div className="booking_links">
                                    {booking.status === BookingStatus.CLOSED && !hasRated && role === Role.CLIENT && (
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                setIsModalHidden(false);
                                                setSelectedBookingId(booking.id);
                                            }}
                                            className="btn btn--primary"
                                        >
                                            {intl.formatMessage("booking.list.links.label.show.rating")}
                                        </Button>
                                    )}
                                    {queryString.get("filter_status") === BookingStatus.TO_VALIDATE &&
                                        role === Role.PROVIDER &&
                                        !booking.booking_validation.client && (
                                            <div
                                                className="italic text-center padding-left--3 padding-right--3"
                                                style={{ fontSize: "14px" }}
                                            >
                                                {intl.formatMessage("booking.list.links.label.waiting")}
                                            </div>
                                        )}
                                    {queryString.get("filter_status") === BookingStatus.TO_VALIDATE &&
                                        role === Role.CLIENT &&
                                        !booking.booking_validation.client && (
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    setIsHiddenValidateServiceModal(false);
                                                    setSelectedBookingId(booking.id);
                                                }}
                                                className="btn btn--primary"
                                            >
                                                {intl.formatMessage("booking.list.links.label.validate.booking")}
                                            </Button>
                                        )}
                                    {queryString.get("filter_status") === BookingStatus.TO_VALIDATE &&
                                        ((role === Role.CLIENT &&
                                            booking.booking_validation?.client?.status ===
                                                BookingValidationStatus.DRAFT) ||
                                            (role === Role.PROVIDER &&
                                                booking.booking_validation?.provider?.status ===
                                                    BookingValidationStatus.DRAFT)) && (
                                            <Button
                                                type="button"
                                                onClick={() => navigate(buildRoute.myReservationResume(booking.id))}
                                                className="btn btn__outline--primary btn--white"
                                            >
                                                {intl.formatMessage("booking.list.links.label.pay.booking")}
                                            </Button>
                                        )}
                                    {queryString.get("filter_status") === BookingStatus.PENDING && (
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                role === Role.CLIENT
                                                    ? setShowCancelBookingModal(true)
                                                    : setIsHiddenRefuseServiceModal(false);
                                                setSelectedBookingId(booking.id);
                                            }}
                                            className={
                                                role === Role.PROVIDER && bookingDateTime.diff(moment(), "hours") === 0
                                                    ? ""
                                                    : "btn btn--cancel"
                                            }
                                            disabled={
                                                role === Role.PROVIDER && bookingDateTime.diff(moment(), "hours") === 0
                                            }
                                        >
                                            {role === Role.PROVIDER
                                                ? bookingDateTime.diff(moment(), "hours") === 0
                                                    ? intl.formatMessage(
                                                          "booking.list.links.label.provider_cancel.booking.disabled",
                                                      )
                                                    : intl.formatMessage(
                                                          "booking.list.links.label.provider_cancel.booking",
                                                      )
                                                : intl.formatMessage("booking.list.links.label.cancel.booking")}
                                        </Button>
                                    )}
                                    {queryString.get("filter_status") === BookingStatus.TO_VALIDATE &&
                                        booking.booking_validation?.client?.status &&
                                        booking.booking_validation?.client?.status ===
                                            BookingValidationStatus.NON_VALIDATED && (
                                            <div
                                                className="italic text-center text-color--danger padding-left--3 padding-right--3"
                                                style={{ fontSize: "14px" }}
                                            >
                                                En cours de contestation...
                                            </div>
                                        )}
                                    <NavLink
                                        to={`${booking.id}/details`}
                                        className="btn btn__outline--primary btn--white"
                                    >
                                        {intl.formatMessage("booking.list.links.label.show.booking")}
                                    </NavLink>
                                    <NavLink
                                        to={buildRoute.messaging(booking.service_id, booking.client_id)}
                                        className="btn btn__outline--primary btn--white"
                                        target="_blank"
                                    >
                                        {intl.formatMessage("booking.list.links.label.show.access_message")}
                                    </NavLink>
                                    {queryString.get("filter_status") === BookingStatus.TO_VALIDATE &&
                                        role === Role.CLIENT &&
                                        !booking.booking_validation.client && (
                                            <div
                                                className="italic text-center text-color--danger padding-left--3 padding-right--3"
                                                style={{ fontSize: "14px" }}
                                            >
                                                <a
                                                    href="javascript:void(0)"
                                                    onClick={() => {
                                                        setIsHiddenRefuseServiceModal(false);
                                                        setSelectedBookingId(booking.id);
                                                    }}
                                                >
                                                    {intl.formatMessage("booking.list.links.label.contest.booking")}
                                                </a>
                                            </div>
                                        )}
                                    {booking.status !== BookingStatus.CLOSED && (
                                        <div className="comment italic">
                                            {role === Role.CLIENT && "La facture "}
                                            {role === Role.PROVIDER && "Le relevé "}
                                            sera disponible lorsque la prestation sera terminée et validée...
                                        </div>
                                    )}
                                    {booking.status === BookingStatus.CLOSED &&
                                        (role === Role.CLIENT && booking.invoices_count > 0 ? (
                                            <Button
                                                className="btn__icon btn__icon--text"
                                                onClick={() => handleDownload(booking)}
                                            >
                                                <span className="icon icon-download"></span>
                                                <span>
                                                    {booking.invoices_count > 1
                                                        ? intl.formatMessage(
                                                              "booking.list.links.label.download.invoices",
                                                          )
                                                        : intl.formatMessage(
                                                              "booking.list.links.label.download.invoice",
                                                          )}
                                                </span>
                                            </Button>
                                        ) : role === Role.PROVIDER ? (
                                            <Button
                                                className="btn__icon btn__icon--text"
                                                onClick={() => handleDownloadSalesSuccession(booking)}
                                            >
                                                <span className="icon icon-download"></span>
                                                <span>
                                                    {intl.formatMessage(
                                                        "booking.list.links.label.download.sales_succession",
                                                    )}
                                                </span>
                                            </Button>
                                        ) : null)}
                                </div>
                            </div>
                            {booking.status === BookingStatus.CLOSED &&
                                selectedBookingId === booking.id &&
                                !hasRated && (
                                    <RatingModal
                                        isHidden={isModalHidden}
                                        setIsHidden={setIsModalHidden}
                                        booking={booking}
                                        dateFormatter={formatDate}
                                        role={role}
                                    />
                                )}
                            {selectedBookingId === booking.id && (
                                <React.Fragment>
                                    <ValidateBookingModal
                                        isHiddenValidateServiceModal={isHiddenValidateServiceModal}
                                        setIsHiddenValidateServiceModal={setIsHiddenValidateServiceModal}
                                        booking={booking}
                                        dateFormatter={formatDate}
                                        role={role}
                                        startTimeSplit={startTimeSplit}
                                    />
                                    <RefuseServiceModal
                                        isHiddenRefuseServiceModal={isHiddenRefuseServiceModal}
                                        setIsHiddenRefuseServiceModal={setIsHiddenRefuseServiceModal}
                                        booking={booking}
                                        dateFormatter={formatDate}
                                        role={role}
                                        startTimeSplit={startTimeSplit}
                                    />
                                    <ConfirmModal
                                        show={showCancelBookingModal}
                                        onClose={() => setShowCancelBookingModal(false)}
                                        onConfirm={() => handleConfirmCancelModal()}
                                        title={intl.formatMessage("booking.list.modal.cancel.booking.title")}
                                        message={
                                            bookingDateTime.diff(moment(), "hours") === 0
                                                ? intl.formatMessage("booking.list.modal.cancel.booking.message") +
                                                  " Attention, vous ne pourrez pas être remboursé car la prestation a lieu dans moins d'1h !"
                                                : intl.formatMessage("booking.list.modal.cancel.booking.message")
                                        }
                                        confirmButton={intl.formatMessage("booking.list.links.label.cancel.booking")}
                                        cancelClassName="btn btn--cancel"
                                    />
                                </React.Fragment>
                            )}
                        </div>
                    );
                }}
            >
                {[]}
            </ListItemWithIds>
        </React.Fragment>
    );
};

export default List;
